import React from 'react';
import {
    FaIdBadge, FaStethoscope, FaFlask, FaMapMarkerAlt, FaGraduationCap, FaLink
} from 'react-icons/fa';
import './ResearcherProfileView.css';

const ResearcherProfileView = ({ researcher }) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    // Adaptar los datos del objeto researcher (User con Profile y ProfessionalProfile)
    const profile = researcher?.profile || {};
    const profProfile = researcher?.professionalProfile || {};

    const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || researcher?.name || 'Investigador';
    const profession = profProfile.profession || researcher?.role || 'Investigador';
    const institution = profProfile.institution || 'Institución no especificada';
    const bio = profile.bio || 'Este investigador aún no ha añadido una biografía a su perfil.';

    const specialties = Array.isArray(profProfile.specialties) ? profProfile.specialties : [];
    const researchAreas = Array.isArray(profProfile.researchAreas) ? profProfile.researchAreas : [];

    // Extracción defensiva de artículos y proyectos
    const articles = Array.isArray(profile.articles) ? profile.articles :
        (Array.isArray(researcher?.articles) ? researcher.articles : []);

    const projects = Array.isArray(profile.projects) ? profile.projects :
        (Array.isArray(researcher?.projects) ? researcher.projects : []);

    console.log('🔍 [ResearcherProfileView] Debug Data:', {
        researcherId: researcher?.id,
        profileId: profile?.id,
        articlesCount: articles.length,
        projectsCount: projects.length,
        articlesData: articles,
        projectsData: projects
    });

    // Resolver URL de imagen
    const photoUrl = profile.photoUrl
        ? (profile.photoUrl.startsWith('http') ? profile.photoUrl : `${backendUrl.replace('/api', '')}${profile.photoUrl}`)
        : researcher?.img;

    const initials = (profile.firstName?.charAt(0) || '') + (profile.lastName?.charAt(0) || '') || researcher?.avatar || fullName.charAt(0);

    return (
        <div className="rpv-container">
            <div className="rpv-header">
                <div className="rpv-avatar-large">
                    {photoUrl ? (
                        <img src={photoUrl} alt={fullName} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                        <div className="rpv-initials-placeholder">{initials}</div>
                    )}
                </div>
                <div className="rpv-header-info">
                    <h1>{fullName}</h1>
                    <p className="rpv-role">{profession}</p>
                    <div className="rpv-meta-row">
                        <span><FaMapMarkerAlt /> {profile.country || 'Ubicación no especificada'}</span>
                        <span><FaGraduationCap /> {institution}</span>
                    </div>
                </div>
            </div>

            <div className="rpv-content">
                <div className="rpv-main">
                    <section className="rpv-section">
                        <h2>Sobre mí</h2>
                        <p className="rpv-bio">{bio}</p>
                    </section>

                    {(specialties.length > 0 || researchAreas.length > 0) && (
                        <section className="rpv-section">
                            <h2>Especialidades y Áreas</h2>
                            <div className="rpv-tags-row">
                                {specialties.length > 0 && (
                                    <div className="rpv-tag-group">
                                        <h3><FaStethoscope /> Especialidades</h3>
                                        <div className="rpv-tags">
                                            {specialties.map((s, i) => <span key={i} className="rpv-tag spec">{s}</span>)}
                                        </div>
                                    </div>
                                )}
                                {researchAreas.length > 0 && (
                                    <div className="rpv-tag-group">
                                        <h3><FaFlask /> Líneas de Investigación</h3>
                                        <div className="rpv-tags">
                                            {researchAreas.map((a, i) => <span key={i} className="rpv-tag area">{a}</span>)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    <section className="rpv-section">
                        <h2>Publicaciones Recientes</h2>
                        {articles.length > 0 ? (
                            <div className="rpv-list">
                                {articles.map((art, i) => (
                                    <div key={i} className="rpv-item">
                                        <div className="rpv-item-icon"><FaFileAlt /></div>
                                        <div className="rpv-item-content">
                                            <h4>{art.title}</h4>
                                            <p>{art.journal} · {art.publicationDate || 'Sin fecha'}</p>
                                        </div>
                                        {art.link && <a href={art.link} target="_blank" rel="noopener noreferrer" className="rpv-btn-link"><FaLink /></a>}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="rpv-empty-msg">No hay publicaciones registradas.</p>
                        )}
                    </section>

                    <section className="rpv-section">
                        <h2>Proyectos</h2>
                        {projects.length > 0 ? (
                            <div className="rpv-list">
                                {projects.map((proj, i) => (
                                    <div key={i} className="rpv-item">
                                        <div className="rpv-item-icon"><FaIdBadge /></div>
                                        <div className="rpv-item-content">
                                            <h4>{proj.title}</h4>
                                            <p>{proj.startDate} - {proj.endDate || 'Presente'}</p>
                                            <p className="rpv-item-desc">{proj.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="rpv-empty-msg">No hay proyectos registrados.</p>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

// Internal Mock Icon for completeness
const FaFileAlt = () => (
    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
        <path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm64 236c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-64c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-64c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"></path>
    </svg>
);

export default ResearcherProfileView;
